import { Component, input, model } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { GENERIC_ASSISTANT_CONFIGURATION_FILENAME } from '@shared';
import { AssistantBuilderFileUploadComponent } from './assistant-builder-file-upload.component';
import { skillMdConfigurationFileValidator } from './assistant-builder-skill-md-config.validators';
import { AssistantBuilderTraitGenericFilesValue } from './assistant-builder-trait-generic-form.validators';

export type AssistantBuilderTraitComparisonForm = {
  [K in keyof AssistantBuilderTraitComparisonFormValues]: FormControl<
    AssistantBuilderTraitComparisonFormValues[K]
  >;
};

/**
 * Comparison authors a skill.md like generic/compliance (SB-3325 T4/KD-3):
 * the four free-text instruction fields are replaced by the configuration
 * file; only the behavioral toggle stays in traitConfig.
 */
export interface AssistantBuilderTraitComparisonFormValues {
  removePromptInput: boolean;
  configurationFile: AssistantBuilderTraitGenericFilesValue;
}

@Component({
  selector: 'squadbox-assistant-builder-trait-comparison-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    fromSeriousSystem.ToggleComponent,
    AssistantBuilderFileUploadComponent,
  ],
  template: `
    @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <!-- skill.md configuration file (Instructions) FIRST — mirrors the
           generic form and the Figma section order (11472:12858). -->
      <div class="flex flex-col gap-2">
        <squadbox-assistant-builder-file-upload
          [label]="
            'ASSISTANTS.SAVE_MODAL.FORM.COMPARISON.CONFIGURATION_FILE.LABEL'
              | translate
          "
          [placeholder]="
            'ASSISTANTS.SAVE_MODAL.FORM.COMPARISON.CONFIGURATION_FILE.PLACEHOLDER'
              | translate
          "
          filePickerButtonIcon="plus"
          formControlName="configurationFile"
          [fileSupportedExtensions]="configurationFileSupportedExtensions"
          [transactionId]="fileUploadTransactionId()"
        ></squadbox-assistant-builder-file-upload>
        <ul class="typo-caption text-neutral-700 mt-0 space-y-1 list-disc pl-5">
          <li
            [innerHTML]="
              'ASSISTANTS.SAVE_MODAL.FORM.COMPARISON.CONFIGURATION_FILE.HINT'
                | translate: configurationFileTranslateParams
            "
          ></li>
        </ul>
      </div>

      <!-- Divider -->
      <div class="py-2"><div class="border-t border-neutral-200"></div></div>

      <!-- Disable message input — LAST section per Figma (11472:12858), small
           toggle matching the multilingual switch. -->
      <sd-toggle
        size="sm"
        formControlName="removePromptInput"
        [label]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPARISON.REMOVE_PROMPT_INPUT.LABEL'
            | translate
        "
        [subtext]="
          'ASSISTANTS.SAVE_MODAL.FORM.COMPARISON.REMOVE_PROMPT_INPUT.DESCRIPTION'
            | translate
        "
      />
    </form>
  `,
})
export class AssistantBuilderTraitComparisonFormComponent {
  public readonly configurationFileTranslateParams = {
    configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  } as const;

  public readonly fileUploadTransactionId = input.required<string>();

  public readonly configurationFileSupportedExtensions = ['md'];

  public readonly form = model<FormGroup<AssistantBuilderTraitComparisonForm>>(
    new FormGroup<AssistantBuilderTraitComparisonForm>(
      {
        removePromptInput: new FormControl(false, {
          nonNullable: true,
        }),
        configurationFile:
          new FormControl<AssistantBuilderTraitGenericFilesValue>(
            {
              value: [],
              disabled: false,
            },
            { nonNullable: true },
          ),
      },
      {
        validators: [skillMdConfigurationFileValidator('configurationFile')],
      },
    ),
  );
}
